/**
 * ExecutiveChartControls — the /admin executive control bar (card 3871; rewritten by card 5336).
 *
 * Split out of `ExecutiveCharts.jsx` rather than grown into it: that file was 276 lines against a
 * 300-line house standard, and the grain + view controls do not fit. Card 3871's own note calls
 * this split ("split the control bar into a sibling component rather than growing the file").
 *
 * ⛔ PRESENTATIONAL ONLY — every selected key lives in `AdminDashboard`, so ONE state drives the
 *   KPI row's fetch and the charts. That shared state is what makes it impossible for the cards
 *   and the charts to disagree about a period, a grain or a population.
 *
 * ⛔ THE THREE GROUPS ARE ORTHOGONAL AND ALL THREE STAY. Period is the WINDOW, granularity is the
 *   GRAIN, view is which population you are looking at. Folding any pair together would delete a
 *   capability, not simplify one.
 *
 * 🔑 CARD 5336 — the three groups are native dropdowns, ⛔ no longer pill buttons. The twelve pills
 *   (7 + 3 + 2) cannot fit a 390px phone on one row at any legible size; a dropdown is one field of
 *   fixed width however many options it holds, and on a phone it opens the OS picker — the most
 *   compact and most tappable control available. It also needs no open/close state, no outside-click
 *   handler, no portal and no focus trap, so it cannot reproduce the recorded `agent-browser click`
 *   hang on a backdrop covering the target, and keyboard + screen-reader behaviour comes for free.
 *   A native dropdown is already this admin panel's design language — 20 admin pages use one.
 *
 * ⛔ THE THREE CONTROLS ARE WRITTEN OUT, not folded into a shared primitive. They are not the same
 *   control three times: each binds a DIFFERENT guard to a DIFFERENT handler over a DIFFERENT key
 *   space, and that binding is the only thing in this file worth reading. The pill version shared a
 *   primitive because its markup was intricate (a button per option, `aria-pressed`, an active
 *   class); a label over a field is not, so the indirection now costs more than it saves.
 */

import {
  EXECUTIVE_PERIODS, resolveOfferedPeriod,
  EXECUTIVE_GRANULARITIES, resolveOfferedGranularity,
  EXECUTIVE_VIEWS, resolveOfferedView
} from '../../utils/executiveChartModel';
import styles from './ExecutiveCharts.module.css';

/**
 * The ONE control bar above the executive block.
 *
 * 🪤 Every read goes through a `resolveOffered*` guard. A selector whose current key is not among
 *   its options renders nothing selected and every chart empty, WITH ZERO CONSOLE ERRORS — and
 *   for granularity it is worse than cosmetic: the endpoint answers 400 on an unknown grain, so
 *   an unguarded key blanks the whole executive block. The guard is named at BOTH the wrapper's
 *   `data-active` and the field's `value`: it is pure and referentially transparent, so the second
 *   mention is the same expression, and it makes the attribute an outside reader uses and the
 *   selection a user sees unable to disagree.
 *
 * 🪤 A group's `data-testid` must not be a prefix of another's — `[data-testid^="executive-kpi-"]`
 *   already matches 8 elements rather than 6 on this page for exactly that reason. The wrapper ids
 *   (`…-control`) and the field ids (`…-select`) are therefore siblings, never nested prefixes.
 *
 * ⛔ THE OPTION LISTS ARE MAPPED FROM THE FROZEN KEY SPACES, never re-typed — that is what keeps
 *   the offered keys, the guards and the server's accepted keys single-sourced.
 */
export const ExecutiveControlBar = ({
  period, onPeriodChange,
  granularity, onGranularityChange,
  view, onViewChange,
  disabled = false
}) => (
  <div className={styles.controlBar}>
    {/* The page already carries a trailing-days filter ("Last 7 Days"…) for the legacy chart row.
        Two unlabelled control rows stacked together are indistinguishable — this heading is what
        tells a reader which block this control governs. */}
    <h2 className={styles.sectionTitle}>Executive Overview</h2>
    <div className={styles.controlGroups}>
      {/* Each field keeps a VISIBLE label above it as well as an `aria-label`. An unlabelled strip
          loses the WINDOW-vs-GRAIN distinction, which is the whole reason the grain control exists
          separately from the period one. `htmlFor` makes the visible label the programmatic one
          too, so the association survives for a pointer user and a screen-reader user alike. */}
      <div className={styles.control} data-testid="executive-period-control" data-active={resolveOfferedPeriod(period)}>
        <label className={styles.groupLabel} htmlFor="executive-period-select">Period</label>
        <select
          id="executive-period-select"
          className={styles.select}
          data-testid="executive-period-select"
          aria-label="Period"
          disabled={disabled}
          value={resolveOfferedPeriod(period)}
          onChange={(e) => onPeriodChange(e.target.value)}
        >
          {EXECUTIVE_PERIODS.map(({ key, label }) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      <div className={styles.control} data-testid="executive-granularity-control" data-active={resolveOfferedGranularity(granularity)}>
        <label className={styles.groupLabel} htmlFor="executive-granularity-select">Grain</label>
        <select
          id="executive-granularity-select"
          className={styles.select}
          data-testid="executive-granularity-select"
          aria-label="Grain"
          disabled={disabled}
          value={resolveOfferedGranularity(granularity)}
          onChange={(e) => onGranularityChange(e.target.value)}
        >
          {EXECUTIVE_GRANULARITIES.map(({ key, label }) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      <div className={styles.control} data-testid="executive-view-control" data-active={resolveOfferedView(view)}>
        <label className={styles.groupLabel} htmlFor="executive-view-select">View</label>
        <select
          id="executive-view-select"
          className={styles.select}
          data-testid="executive-view-select"
          aria-label="View"
          disabled={disabled}
          value={resolveOfferedView(view)}
          onChange={(e) => onViewChange(e.target.value)}
        >
          {EXECUTIVE_VIEWS.map(({ key, label }) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

export default ExecutiveControlBar;
